// hamburger-toggle-component.ts
import { CanvasService } from "appworks/graphics/canvas/canvas-service";
import { Orientation } from "appworks/graphics/canvas/orientation";
import { ButtonElement, ButtonEvent } from "appworks/graphics/elements/button-element";
import { DualPosition } from "appworks/graphics/pixi/dual-position";
import { CenterPivot } from "appworks/graphics/pixi/group";
import { Sprite } from "appworks/graphics/pixi/sprite";
import { Services } from "appworks/services/services";
import { SoundService } from "appworks/services/sound/sound-service";
import { uiFlags } from "appworks/ui/flags/ui-flags";
import { Timer } from "appworks/utils/timer";
import { Tween } from "appworks/utils/tween";

/**
    HamburgerState enum specifies the two states of HamburgerToggleComponent - Expanded and Collapsed.
    @enum {number}
*/
enum HamburgerState {
    EXPANDED,
    COLLAPSED
}

/**
 * Handles the functionality of the Hamburger Menu component within the UI. The Hamburger Menu is a set of buttons that will expand from a single button when clicked,
 * and then collapse back into the button when the user opts to close the menu.
 *
 * @property {ButtonElement} expandButton - The button that users will click on to expand the Hamburger Menu.
 * @property {ButtonElement} collapseButton - The button that users will click on to collapse the Hamburger Menu.
 * @property {ButtonElement[]} hamburgerButtons - An array of ButtonElements that represents all the menu buttons included in the Hamburger Menu.
 *
 * @property {Tween[]} expandTweens - An array of Tween objects, which manage the expand animation of the Hamburger Menu items.
 * @property {Tween[]} collapseTweens - An array of Tween objects, which manage the collapse animation of the Hamburger Menu items.
 *
 * @property {Map<ButtonElement, DualPosition>} hamburgerItemPairs - A Map that pairs each ButtonElement with its DualPosition.
 * @property {HamburgerState} hamburgerState - The current state of the Hamburger Menu (collapsed or expanded).
 * @property {number} menuExpandRateInMS - The rate at which the menu expands, in milliseconds.
 *
 * @property {boolean} isButtonClick - Indicates if the menu state change was triggered by a button click.
 * @property {boolean} isOnCooldown - Indicates if the menu is currently on a cooldown period after a state change.
 *
 * @constructor
 * The constructor accepts four parameters:
 * `expandButton`, `collapseButton`, `hamburgerButtons`, and `toggleControlInstance`.
 *
 * It sets up the expand and collapse tweens for each button, adjusts the initial position of the buttons,
 * and attaches the necessary event listeners for the expand and collapse buttons.
 * It also sets up the rotation for the collapse button based on the current orientation of the canvas, and adds listeners
 * to change the rotation whenever the orientation of the canvas changes.
 *
 * Lastly, it sets the initial state of the Hamburger Menu to 'collapsed', and adds a listener to the global button click signal
 * to automatically collapse the menu whenever a button click event is detected outside the menu.
 */
export class HamburgerToggleComponent {
    protected readonly menuExpandRateInMS: number = 250;
    protected readonly buttonGapSize = 15;

    protected readonly soundButtonPrefix = "sound";

    protected expandButton: ButtonElement;
    protected collapseButton: ButtonElement;
    protected hamburgerButtons: ButtonElement[];

    protected expandTweens: Tween[] = [];
    protected collapseTweens: Tween[] = [];

    protected hamburgerState: HamburgerState;

    protected isButtonClick: boolean = false;

    protected isOnCooldown: boolean = false;

    /**
     * This constructor will take as parameters all of the buttons we want to add to the menu, and create tween animations for them. When adding the buttons into
     * photoshop, the buttons should be configured in the "expanded" position. This constructor will use the expanded positions of the buttons in photoshop
     * and create a tween for the elements to the expand button's position for the collapse animation, and to the original photoshop positions for the expand animation.
     *
     * @remarks
     * The positioning of buttons in the expanded menu relies on the fact that the two sound buttons (on/off toggle) are added last to the hamburgerButtons array
     * and appear at the top of the expanded menu. They will be the same size/position and only one will be active at a time.
     *
     * @param {ButtonElement} expandButton - This is the button the user will click on to expand the hamburger menu.
     * @param {ButtonElement} collapseButton - This is the button that the user will click on to collapse the hamburger menu.
     * @param {ButtonElement[]} hamburgerButtons - This array will hold all of the menu buttons we want to include inside of the hamburger menu.
     */
    constructor(expandButton: ButtonElement, collapseButton: ButtonElement, hamburgerButtons: ButtonElement[], protected toggleControlInstance) {
        const soundButtonX: { landscape: number, portrait: number } = { landscape: undefined, portrait: undefined };
        const soundButtonY: { landscape: number, portrait: number } = { landscape: undefined, portrait: undefined };

        hamburgerButtons.forEach((item, idx) => {
            const sourcePosition = expandButton.dualPosition;
            const destination = expandButton.dualPosition.clone();

            item.setDualTransform(sourcePosition);

            [Orientation.LANDSCAPE, Orientation.PORTRAIT].forEach((orientation) => {
                const numGaps = idx + 1;
                const isProcessingLandscape = orientation === Orientation.LANDSCAPE;
                const xOffset = isProcessingLandscape ? 0 : item.dualPosition[orientation].width * numGaps + this.buttonGapSize * numGaps;
                const yOffset = isProcessingLandscape ? item.dualPosition[orientation].height * numGaps + this.buttonGapSize * numGaps : 0;
                const exTween = new Tween(item[orientation]);

                let calculatedX = destination[orientation].x + xOffset;
                let calculatedY = destination[orientation].y - yOffset;
                if (item.name.includes(this.soundButtonPrefix)) {
                    if (soundButtonX[orientation] != undefined) {
                        calculatedX = soundButtonX[orientation];
                    } else {
                        soundButtonX[orientation] = calculatedX;
                    }

                    if (soundButtonY[orientation] != undefined) {
                        calculatedY = soundButtonY[orientation];
                    } else {
                        soundButtonY[orientation] = calculatedY;
                    }
                }

                exTween.to({x: calculatedX, y: calculatedY}, this.menuExpandRateInMS);
                exTween.onComplete(() => {
                    exTween.stop();
                });
                this.expandTweens.push(exTween);

                const colTween = new Tween(item[orientation]);
                colTween.to({x: sourcePosition[orientation].x, y: sourcePosition[orientation].y}, this.menuExpandRateInMS);
                colTween.onComplete(() => {
                    colTween.stop();
                    item.setVisible(false);
                });
                this.collapseTweens.push(colTween);
            });
        });
        this.expandButton = expandButton;
        this.collapseButton = collapseButton;
        this.hamburgerButtons = hamburgerButtons;

        CenterPivot(this.expandButton.children[0] as Sprite);
        CenterPivot(this.collapseButton.children[0] as Sprite);

        this.expandButton.on(ButtonEvent.CLICK, this.buttonHandler);

        this.rotateCollapseButton(Services.get(CanvasService).orientation);
        Services.get(CanvasService).onOrientationChange.add((orientation) => this.rotateCollapseButton(orientation));

        this.setState(HamburgerState.COLLAPSED);

        ButtonElement.globalButtonClickSignal.add(this.forceCollapseMenu, this);
    }

    /**
     * The rotateCollapseButton method is called when the Canvas orientation is changed.
     *
     * This method is necessary since for whatever reason photoshop does not allow you to change the orientation of the same
     * art asset with the same name in multiple places on our PSD. This function assumes that the portrait icon is the correct orientation (pointed to the right),
     * and that the landscape orientation button needs to be pointed upwards (-Math.PI / 2). (The landscape icon will point to the right like the portrait icon if this
     * code does not execute).
     *
     * Feel free to override this to whatever orientation you need the collapse button to be facing.
     *
     * @method
     * @protected
     * @param {Orientation} orientation - This is the orientation of the game at the point this method is invoked (It's registered as a callback to the orientation change
     *                                      signal)
     */
    protected rotateCollapseButton(orientation: Orientation) {
        if(this.collapseButton.children.length == 0) {
            return;
        }
        switch(orientation){
            case Orientation.LANDSCAPE:
                (this.collapseButton.children[0] as Sprite).rotation = Math.PI / 2;
                break;
            case Orientation.PORTRAIT:
                (this.collapseButton.children[0] as Sprite).rotation = Math.PI;
                break;
        }
    }

    /**
    *
    *    The destroy method is called when the HamburgerToggleComponent needs to be destroyed.
    *    @method
    *    @public
    */
    public destroy() {
        // TODO: Reset buttons to expanded configuration??
        this.expandButton.off(ButtonEvent.CLICK, this.buttonHandler);
        this.collapseButton.off(ButtonEvent.CLICK, this.buttonHandler);
    }

    /**
    *
    *    The setVisible method is called when the visibility of the HamburgerToggleComponent changes.
    *    @method
    *    @public
    *    @param {boolean} visible - The new visible state of the HamburgerToggleComponent.
    */
    public setVisible(visible: boolean) {
        if (visible) {
            this.updateMenuButtons();
        } else {
            this.expandButton.setVisible(false);
            this.collapseButton.setVisible(false);
        }
    }

    /**
    *    The buttonHandler method is called whenever the expandButton or collapseButton is clicked.
    *    @method
    *    @protected
    */
    protected buttonHandler = (button?: ButtonElement) => {
        uiFlags.closeMenus();
        this.nextState();
    }

    /**
    *    The forceCollapseMenu method will determine if the button should collapse the menu,
    *    if the button should collapse the menu it will set the state to collapsed.
    *    @method
    *    @protected
    *    @param {ButtonElement} button
    */
    protected forceCollapseMenu(button?: ButtonElement) {
        this.isButtonClick = true;
        if(button && button.shouldCollapseMenu) {
            this.setState(HamburgerState.COLLAPSED);
        }
    }


    /**
    *    The nextState method is called whenever the expandButton or collapseButton is clicked.
    *    @method
    *    @protected
    */
    protected nextState() {
        switch (this.hamburgerState) {
            case HamburgerState.EXPANDED:
                this.setState(HamburgerState.COLLAPSED);
                break;

            case HamburgerState.COLLAPSED:
                this.setState(HamburgerState.EXPANDED);
                break;
        }
    }

    /**
    *    The globalClickListener method will be fired off if the user clicks on the screen. Here we
    *    check to see if the click was a button click. If it wasn't (the user clicked in empty space) we
    *    collapse the menu here.
    *    @method
    *    @protected
    */
    protected globalClickListener() {
        if(!this.toggleControlInstance.enabled) {
            return;
        }
        if(!this.isButtonClick) {
            if(!this.isOnCooldown) {
                Services.get(SoundService).customEvent("button_press_close");
            }
            this.setState(HamburgerState.COLLAPSED);
        }
        this.isButtonClick = false;
    };

    /**
    *    This method toggles the cooldown boolean flag to prevent the menu from being opened during its
    *    animation (double tween error will crash game).
    *    @method
    *    @protected
    */
    protected startCoolingDown() {
        this.isOnCooldown = false;
    }

    /**
    *    The setState method is called whenever the expandButton or collapseButton is clicked.
    *    @method
    *    @protected
    *    @param {HamburgerState} state - This is the state we will move to when this method is invoked.
    *
    */
    protected setState(state?: HamburgerState) {
        if(this.isOnCooldown || state == this.hamburgerState) {
            return;
        }
        this.isOnCooldown = true;
        Timer.setTimeout(this.startCoolingDown.bind(this), this.menuExpandRateInMS + 50);
        switch (state) {
            case HamburgerState.EXPANDED:
                Services.get(CanvasService).onCanvasClicked.add(this.globalClickListener, this);
                this.expandTweens.forEach(anim => {
                    anim.start();
                });
                break;

            case HamburgerState.COLLAPSED:
                Services.get(CanvasService).onCanvasClicked.remove(this.globalClickListener, this);
                this.collapseTweens.forEach(anim => {
                    anim.start();
                });
                break;
        }
        this.hamburgerState = state;
        this.updateMenuButtons();
    }

    /**
     * This method updates the visibility of the buttons. It is invoked on startup and any time the
     * state changes.
     * @method
     * @protected
     */
    protected updateMenuButtons() {
        this.setVisible(false);

        switch (this.hamburgerState) {
            case HamburgerState.COLLAPSED:
                this.expandButton.setVisible(true);
                break;

            case HamburgerState.EXPANDED:
                this.hamburgerButtons.forEach((button) => {
                    let toVisible = true;
                    if(button.name.includes(this.soundButtonPrefix)) {
                        toVisible = button.isEnabled();
                    }
                    button.setVisible(toVisible);
                });
                this.collapseButton.setVisible(true);
                break;
        }
    }
}
